import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  LoadingButton,
  LocalizationProvider,
  MobileDatePicker,
  TimePicker,
} from '@mui/lab';
import {
  Autocomplete,
  FormControl,
  Grid,
  InputAdornment,
  Stack,
  TextFieldProps,
} from '@mui/material';
import { DateRange as DateRangeIcon } from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';

import { currencyOptions, dayListOptions } from '../../../../../../constants';
import {
  getCoursesState,
  TUpdateCourseAsAdmin,
} from '../../../../../../store/V2/CoursesSlice';
import { updateCourseAsAdminSavingSlice } from '../../../../../../store/V2/SavingSlice';
import { useAppSelector } from '../../../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';

interface Step1Props {
  moveToNextStep: () => void;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  classDay: TSelectOption | null;
  classTime: Moment | null;
  courseStart: Moment | null;
  courseEnd: Moment | null;
  amount: string;
  selectedCurrency: TSelectOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  classDay: null,
  classTime: null,
  courseStart: null,
  courseEnd: null,
  selectedCurrency: null,
  amount: '',
};

const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const Step1: React.FC<Step1Props> = ({ moveToNextStep }) => {
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const courseData = coursesState.adminCourse;
  const isNextDisabled =
    isLoading ||
    !state.classDay ||
    !state.classTime ||
    !state.courseStart ||
    !state.courseEnd ||
    !state.selectedCurrency ||
    !state.amount.trim().length ||
    Number(state.amount) < 1;

  useEffect(() => {
    if (courseData) {
      setState((prevState) => {
        const newState = { ...prevState };

        if (courseData.classDay) {
          newState.classDay = {
            label: courseData.classDay,
            value: courseData.classDay,
          };
        }

        if (courseData.classTime) {
          const _utcMoment = momentTimezone.tz(
            courseData.classTime,
            'HH:mm',
            'UTC'
          );

          newState.classTime = _utcMoment.clone().tz(moment.tz.guess());
        }

        if (courseData.startDate) {
          newState.courseStart = moment(courseData.startDate);
        }

        if (courseData.endDate) {
          newState.courseEnd = moment(courseData.endDate);
        }

        if (courseData.currency) {
          newState.selectedCurrency = {
            label: courseData.currency,
            value: courseData.currency,
          };
        }

        if (courseData.amount) {
          newState.amount = courseData.amount.toString();
        }

        return newState;
      });
    }
  }, []);

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const handleClassDayChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      classDay: value,
    }));
  };

  const handleClassTimeChange = (value: Moment | null) => {
    setState((prevState) => ({
      ...prevState,
      classTime: value,
    }));
  };

  const handleCourseStartDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseStart: value,
      }));
    }
  };

  const handleCourseEndDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        courseEnd: value,
      }));
    }
  };

  const handleCurrencyChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    setState((prevState) => ({
      ...prevState,
      selectedCurrency: value,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    toggleLoading();

    if (courseData) {
      const apiData: TUpdateCourseAsAdmin = {
        _id: courseData._id,
        amount: Number(state.amount),
        isStep1Completed: true,
      };

      if (!courseData.isOneToOne) {
        apiData.isStep2Completed = true;
      }

      if (state.classDay) {
        apiData.classDay = state.classDay.value;
      }

      if (state.classTime) {
        apiData.classTime = state.classTime.clone().utc().format('HH:mm');
      }

      if (state.courseStart) {
        apiData.startDate = state.courseStart.clone().toISOString();
      }

      if (state.courseEnd) {
        apiData.endDate = state.courseEnd.clone().toISOString();
      }

      if (state.selectedCurrency) {
        apiData.currency = state.selectedCurrency.value;
      }

      if (Object.keys(apiData).length > 1) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res: any = await dispatch(
          updateCourseAsAdminSavingSlice(apiData)
        );

        if (
          res.payload &&
          res.payload.templateSteps &&
          res.payload.templateSteps.step1 === 'completed'
        ) {
          moveToNextStep();
        }
      }
    }

    toggleLoading();
  };

  return (
    <div>
      <div style={{ minHeight: '350px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                size="small"
                value={state.classDay}
                options={dayListOptions}
                onChange={handleClassDayChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Class Day"
                    placeholder="Class Day"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <TimePicker
                  value={state.classTime}
                  onChange={handleClassTimeChange}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Class Time"
                      placeholder="Class Time"
                      helperText={
                        state.classTime ? `(${deviceTimezone})` : undefined
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                  disableCloseOnSelect={false}
                  value={state.courseStart}
                  onChange={handleCourseStartDateChange}
                  label="Start Date"
                  inputFormat="YYYY MMM DD"
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Start Date"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DateRangeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <MobileDatePicker
                  disableCloseOnSelect={false}
                  value={state.courseEnd}
                  onChange={handleCourseEndDateChange}
                  label="End Date"
                  inputFormat="YYYY MMM DD"
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="End Date"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end">
                            <DateRangeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCurrency}
                options={currencyOptions}
                onChange={handleCurrencyChange}
                popupIcon={TPAutoCompleteArrowIconComponent}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.value
                }
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Currency *"
                    placeholder="Currency"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="amount"
                type="number"
                size="small"
                label="Amount *"
                placeholder="Amount"
                value={state.amount}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
        </Grid>
      </div>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt="15px"
      >
        <LoadingButton
          variant="text"
          color="primary"
          onClick={handleSubmit}
          disabled={isNextDisabled}
          loading={isLoading}
        >
          Next
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default Step1;
